/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import UnauthorizedError from './UnauthorizedError';
import {fetchWithRetry} from './fetchWithRetry';

export default async function queryGraphQL<TData, TVariables>(
  query: string,
  variables: TVariables,
  requestHeaders: Record<string, string>,
  graphQLEndpoint: string,
  signal?: AbortSignal,
): Promise<TData> {
  const isMutation = /^\s*mutation\b/.test(query);
  const response = await fetchWithRetry(
    graphQLEndpoint,
    {
      headers: requestHeaders,
      method: 'POST',
      body: JSON.stringify({query, variables}),
      signal,
    },
    'query GitHub',
    {
      maxAttempts: isMutation ? 1 : undefined,
      inspectResponse: async candidate => {
        if (!candidate.ok) {
          return false;
        }
        const body = await candidate.json().catch(() => null);
        return (
          body?.errors?.some?.((error: {type?: unknown}) => error.type === 'RATE_LIMITED') === true
        );
      },
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new UnauthorizedError(
        'Your GitHub access token has expired or been revoked. Please sign in again.',
      );
    }
    if (
      response.status === 429 ||
      (response.status === 403 && response.headers.get('x-ratelimit-remaining') === '0')
    ) {
      const reset = response.headers.get('x-ratelimit-reset');
      const resetMessage =
        reset == null ? '' : ` until ${new Date(Number(reset) * 1000).toLocaleString()}`;
      throw new Error(`GitHub API rate limit exceeded${resetMessage}.`);
    }
    return Promise.reject(`HTTP request error: ${response.status}: ${response.statusText}`);
  }

  const json = await response.json();

  if (Array.isArray(json.errors)) {
    return Promise.reject(`Error: ${json.errors[0].message}`);
  }

  return json.data;
}
